import { watch, toValue, type ComputedRef, type Ref } from 'vue'
import { useToast } from './useToast'

/**
 * Interface for stores that support error watching
 */
interface StoreWithError {
  hasError: ComputedRef<boolean> | Ref<boolean> | boolean
  error: ComputedRef<string | null> | Ref<string | null> | string | null
  clearError: () => void
}

/**
 * Sets up a watcher to display store errors as toast notifications
 *
 * Eliminates repeated pattern across views:
 * ```
 * watch(() => store.hasError, (hasError) => {
 *   if (hasError && store.error) {
 *     showError(store.error, 'Error Title')
 *     store.clearError()
 *   }
 * })
 * ```
 *
 * @param store - Store with hasError, error, and clearError
 * @param errorTitle - Title to display in toast notification
 *
 * @example
 * // In component setup
 * const store = useMerchantsStore()
 * useStoreErrorWatch(store, 'Error Loading Merchants')
 *
 * @example
 * // Multiple stores
 * useStoreErrorWatch(merchantsStore, 'Error Loading Merchants')
 * useStoreErrorWatch(locationsStore, 'Error Loading Locations')
 */
export function useStoreErrorWatch(store: StoreWithError, errorTitle: string): void {
  const { showError } = useToast()

  watch(
    () => toValue(store.hasError),
    hasError => {
      const message = toValue(store.error)
      if (hasError && message) {
        showError(message, errorTitle)
        store.clearError()
      }
    }
  )
}
